<template>
    <div class="editor">
        <tinymce :id="id" :init="settings()" :value="value" @input="input"></tinymce>
        <div v-if="modal" class="filemanager-modal">
            <filemanager :modal="true" :media="media"></filemanager>
        </div>
    </div>
</template>

<script>
// Import TinyMCE
import head from 'lodash/head';
import 'tinymce/tinymce';

// Any plugins you want to use has to be imported
import 'tinymce/plugins/advlist';
import 'tinymce/plugins/wordcount';
import 'tinymce/plugins/autolink';
import 'tinymce/plugins/autosave';
import 'tinymce/plugins/charmap';
import 'tinymce/plugins/contextmenu';
import 'tinymce/plugins/emoticons';
// import 'tinymce/plugins/codesample';
// import 'tinymce/plugins/fullscreen';
import 'tinymce/plugins/hr';
// import 'tinymce/plugins/imagetools';
import 'tinymce/plugins/insertdatetime';
import 'tinymce/plugins/link';
import 'tinymce/plugins/media';
// import 'tinymce/plugins/noneditable';
import 'tinymce/plugins/paste';
// import 'tinymce/plugins/print';
import 'tinymce/plugins/searchreplace';
// import 'tinymce/plugins/tabfocus';
import 'tinymce/plugins/template';
import 'tinymce/plugins/textpattern';
import 'tinymce/plugins/visualblocks';
import 'tinymce/plugins/anchor';
import 'tinymce/plugins/autoresize';
// import 'tinymce/plugins/bbcode';
import 'tinymce/plugins/code';
import 'tinymce/plugins/colorpicker';
import 'tinymce/plugins/directionality';
// import 'tinymce/plugins/fullpage';
// import 'tinymce/plugins/help';
import 'tinymce/plugins/image';
// import 'tinymce/plugins/importcss';
// import 'tinymce/plugins/legacyoutput';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/nonbreaking';
// import 'tinymce/plugins/pagebreak';
// import 'tinymce/plugins/preview';
import 'tinymce/plugins/save';
// import 'tinymce/plugins/spellchecker';
import 'tinymce/plugins/table';
import 'tinymce/plugins/textcolor';
// import 'tinymce/plugins/toc';
import 'tinymce/plugins/visualchars';

// Custom plugins
import './tinymce/plugins/floatingtoolbar';
import './tinymce/plugins/linktools';
import './tinymce/plugins/tabletools';

// A theme is also required
import 'tinymce/themes/modern/theme';

// TinyMCE Component
import TinyMCE from '@tinymce/tinymce-vue';

export default {
    name: 'editor',

    components: {
        'tinymce': TinyMCE
    },

    props: {
        'id': {
            type: String,
            required: true
        },
        'value': {
            default: ''
        },
        'plugins': {
            default: function()
            {
                return [
                    'advlist autolink autosave lists link image charmap hr anchor autoresize',
                    'searchreplace wordcount visualblocks visualchars code template floatingtoolbar',
                    'insertdatetime media nonbreaking save table contextmenu directionality',
                    'emoticons paste textcolor colorpicker textpattern linktools tabletools'
                    // 'gallery filemanager mention localautosave'
                ];
            },
            type: Array
        },
        'toolbar1': {
            default: 'undo redo | bold italic | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | tabletools_insert link gallery_insert filemanager_insert | forecolor backcolor | template code',
            type: String
        },
        'toolbar2': {
            default: '',
            type: String
        },
        'otherOptions': {
            default: function()
            {
                return {};
            },
            type: Object
        }
    },

    data()
    {
        return {
            modal: false,
            media: "files",
            filemanager: {
                callback: null,
                value: null,
                meta: null
            }
        };
    },

    mounted()
    {
        this.$events.$on('filemanager-modal-close', this.closeModal);
        this.$events.$on('filemanager-callback-value', this.callbackValue);
    },

    destroyed()
    {
        this.$events.$off('filemanager-modal-close');
        this.$events.$off('filemanager-callback-value');
    },

    methods: {
        settings()
        {
            let $this = this;

            return {
                selector: '#' + this.id,
                skin_url: '/static/skins/lightgray',
                toolbar1: this.toolbar1,
                toolbar2: this.toolbar2,
                plugins: this.plugins,
                templates: this.templates(),
                relative_urls: false,
                paste_data_images: false,
                browser_spellcheck: true,
                entity_encoding: "raw",
                autoresize_min_height: 450,
                autoresize_bottom_margin: 50,
                nowrap: false,
                resize: false,
                floatingtoolbar_top: 60,
                floatingtoolbar_selectors: ['section .content'],
                linktools_toolbar: 'link | unlink',
                table_style_by_css: true,
                // las_seconds: 15,
                // extended_valid_elements : "edito[class|data*],edito-gallery[class|data*],edito-files[class|data*],div[class|data*]",
                // custom_elements: "edito,edito-gallery,edito-files",
                file_picker_callback: function(callback, value, meta)
                {
                    $this.modal = true;
                    $this.filemanager.callback = callback;
                    $this.filemanager.value = value;
                    $this.filemanager.meta = meta;

                    switch (meta.filetype)
                    {
                        case "image":
                            $this.media = "images";
                            break;
                        case "media":
                            $this.media = "media";
                            break;
                        case "file":
                            $this.media = "files";
                            break;
                        default:
                            $this.media = "files";
                            break;
                    }
                },
                ...this.otherOptions
            };
        },

        input(value)
        {
            this.$emit('input', value);
        },

        templates()
        {
            return [
                {
                    title: '1/2',
                    description: this.$t('[[[Wstaw dwie równe kolumny]]]'),
                    content: `
                        <div class="row">
                            <div class="medium-6 small-12 column edito-column">
                                &nbsp;Lorem ipsum...
                            </div>
                            <div class="medium-6 small-12 column edito-column">
                                &nbsp;Lorem ipsum...
                            </div>
                        </div>
                        <p></p>
                    `
                },
                {
                    title: '2/3 - 1/3',
                    description: this.$t('[[[Wstaw dwie kolumny 2/3 oraz 1/3]]]'),
                    content: `
                        <div class="row">
                            <div class="medium-8 small-12 column edito-column">
                                &nbsp;Lorem ipsum...
                            </div>
                            <div class="medium-4 small-12 column edito-column">
                                &nbsp;Lorem ipsum...
                            </div>
                        </div>
                        <p></p>
                    `
                },
                {
                    title: '1/3',
                    description: this.$t('[[[Wstaw trzy kolumny 1/3]]]'),
                    content: `
                        <div class="row">
                            <div class="medium-4 small-12 column edito-column">
                                &nbsp;Lorem ipsum...
                            </div>
                            <div class="medium-4 small-12 column edito-column">
                                &nbsp;Lorem ipsum...
                            </div>
                            <div class="medium-4 small-12 column edito-column">
                                &nbsp;Lorem ipsum...
                            </div>
                        </div>
                        <p></p>
                    `
                },
                {
                    title: '1/4',
                    description: this.$t('[[[Wstaw cztery kolumny 1/4]]]'),
                    content: `
                        <div class="row">
                            <div class="medium-3 small-12 column edito-column">
                                &nbsp;Lorem ipsum...
                            </div>
                            <div class="medium-3 small-12 column edito-column">
                                &nbsp;Lorem ipsum...
                            </div>
                            <div class="medium-3 small-12 column edito-column">
                                &nbsp;Lorem ipsum...
                            </div>
                            <div class="medium-3 small-12 column edito-column">
                                &nbsp;Lorem ipsum...
                            </div>
                        </div>
                        <p></p>
                    `
                },
                {
                    title: '1/3 - 2/3',
                    description: this.$t('[[[Wstaw dwie kolumny 1/3 oraz 2/3]]]'),
                    content: `
                        <div class="row">
                            <div class="medium-4 small-12 column edito-column">
                                &nbsp;Lorem ipsum...
                            </div>
                            <div class="medium-8 small-12 column edito-column">
                                &nbsp;Lorem ipsum...
                            </div>
                        </div>
                        <p></p>
                    `
                },
                {
                    title: this.$t('[[[Ważne informacje]]]'),
                    description: this.$t('[[[Wstaw szablon informacyjny ze zdjęciem]]]'),
                    content: `
                        <div class="row custom-template" data-equalizer="column">
                            <div class="medium-3 small-5 column image-template" data-equalizer-watch="column">
                                <img src="/static/img/logo.png">
                            </div>
                            <div class="medium-9 small-7 column desc" data-equalizer-watch="column">
                                &nbsp;Treść komunikatu
                            </div>
                        </div>
                        <p></p>
                    `
                }
            ];
        },

        callbackValue(files)
        {
            if (this.modal === true)
            {
                let file = head(files);
                let params = this.filemanager;

                if (file)
                {
                    let url = this.$options.filters.baseurl(file.url);
                    let size = this.$options.filters.filesize(file.contentLength);
                    let text = `${file.name} [${size}]`;

                    if (params.meta.filetype === 'file')
                    {
                        params.callback(url, { text: params.meta.text || text, title: file.name });
                    }
                    if (params.meta.filetype === 'image')
                    {
                        params.callback(url, { alt: file.name, width: file.meta.width, height: file.meta.height });
                    }
                    if (params.meta.filetype === 'media')
                    {
                        params.callback(url, { width: 480, height: 270 });
                    }
                }

                this.closeModal();
            }
        },

        closeModal()
        {
            this.modal = false;
        }
    }
};
</script>

<style>
.editor {
    margin-left: 0px;
}
.editor .modal {
    z-index: 65537 !important;
}
.editor .mce-panel {
    border-color: #e1e6ef;
    box-shadow: none;
}
</style>
